import React, { useEffect, useState, useMemo } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  TextField,
  Box,
  Button,
  Grid,
  useTheme
} from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { bindActionCreators } from '@reduxjs/toolkit';
import { State } from '../../state/reducers';
import { Account } from '../../state/types';
import { fetchAccounts } from '../../state/account/action_creator';
import { useAccountContext } from '../../context/AccountContext';

type SortKey = keyof Account | ''; // Safe list of sortable keys

const AccountsTable: React.FC = () => {
  const { openEditAccountModal, openAddAccountModal } = useAccountContext();

  const theme = useTheme()
  const dispatch = useDispatch();
  const fetchAccountsAction = bindActionCreators(fetchAccounts, dispatch);

  const accountList: Account[] = useSelector((state: State) => state.accountState.users || []);

  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<SortKey>('email');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    fetchAccountsAction();
  }, []);

  const handleSort = (key: SortKey) => {
    if (sortBy === key) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDirection('asc');
    }
  };

  const filteredAndSortedAccounts = useMemo(() => {
    const filtered = accountList.filter(user => {
      const query = searchTerm.toLowerCase();
      return (
        user.email.toLowerCase().includes(query) ||
        user.first_name.toLowerCase().includes(query) ||
        user.last_name.toLowerCase().includes(query) ||
        user.phone_number?.toLowerCase().includes(query) ||
        user.role.toLowerCase().includes(query) ||
        user.status?.toLowerCase().includes(query)
      );
    });

    if (!sortBy) return filtered;

    return filtered.sort((a, b) => {
      const aVal = a[sortBy];
      const bVal = b[sortBy];
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortDirection === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }
      return 0;
    });
  }, [accountList, searchTerm, sortBy, sortDirection]);

  const headers: { label: string; key: SortKey }[] = [
    { label: 'Email', key: 'email' },
    { label: 'First Name', key: 'first_name' },
    { label: 'Last Name', key: 'last_name' },
    { label: 'Phone Number', key: 'phone_number' },
    { label: 'Role', key: 'role' },
    { label: 'Status', key: 'status' },
    { label: 'Last Login', key: 'lastLoginDate' },
  ];

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Grid container spacing={2} alignItems="center" justifyContent="space-between">
          <Grid item xs={12} md={6}>
            <TextField
              label="Search"
              variant="outlined"
              size="small"
              fullWidth
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="contained"
              color="primary"
              onClick={openAddAccountModal}
              fullWidth
              sx={{ whiteSpace: 'nowrap' }}
            >
              Add Account
            </Button>
          </Grid>
        </Grid>
      </Box>


      <TableContainer
        component={Paper}
        sx={{
          maxHeight: '48vh', // this limits the visible height
          overflowY: 'auto', // vertical scroll
          overflowX: 'auto',
          maxWidth: '100%',
          scrollbarWidth: 'none', // Firefox
          '&::-webkit-scrollbar': {
            display: 'none',       // Chrome, Safari
          },
        }}
      >
        <Table sx={{ minWidth: 900 }}>
          <TableHead
            sx={{
              position: 'sticky',
              top: 0,
              backgroundColor: theme.palette.background.paper,
              zIndex: 1,
            }}
          >
            <TableRow>
              {headers.map(({ label, key }) => (
                <TableCell key={key}>
                  <TableSortLabel
                    active={sortBy === key}
                    direction={sortBy === key ? sortDirection : 'asc'}
                    onClick={() => handleSort(key)}
                  >
                    {label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredAndSortedAccounts.map(user => (
              <TableRow
                key={user.username}
                hover
                onClick={() => openEditAccountModal(user)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell><Typography>{user.email}</Typography></TableCell>
                <TableCell><Typography>{user.first_name}</Typography></TableCell>
                <TableCell><Typography>{user.last_name}</Typography></TableCell>
                <TableCell><Typography>{user.phone_number}</Typography></TableCell>
                <TableCell><Typography>{user.role}</Typography></TableCell>
                <TableCell><Typography>{user.status}</Typography></TableCell>
                <TableCell><Typography>{user.lastLoginDate}</Typography></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default AccountsTable;
